import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { SITE_NOTICES, SITE_NOTICE_STORAGE_KEY } from "@/lib/company";

// Paths where the banner MUST NOT render (per Phase 3 spec).
const HIDDEN_PREFIXES = ["/admin", "/portal/learn", "/verify"];
// A premium reading pace — one full cycle of the marquee track.
const MARQUEE_DURATION_S = 70;

function shouldHide(pathname) {
    return HIDDEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = (e) => setReduced(e.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return reduced;
}

/**
 * Rich content unit A — rendered inside the moving track. Formatting:
 *   [ IMPORTANT NOTICE pill ]  Official Website Update (italic Fraunces) —
 *   Our previous website, <s>ebconstructionservices.co.uk</s>, has been
 *   retired. You are now visiting the official website of
 *   <strong>EB Construction Ltd</strong>.
 */
function NoticeUnit({ notice }) {
    return (
        <span
            className="site-notice-unit inline-flex items-center gap-3 whitespace-nowrap text-[13px] md:text-[14px]"
            data-testid={`site-notice-item-${notice.id}`}
        >
            <span className="site-notice-pill inline-flex items-center rounded-full border border-brand-gold/40 bg-brand-gold/10 px-2.5 py-0.5 font-mono-tag text-[10px] uppercase tracking-[0.16em] text-brand-gold">
                {notice.label}
            </span>
            <span className="font-display italic text-brand-offwhite">
                {notice.heading}
            </span>
            <span className="text-brand-offwhite/40">—</span>
            <span className="font-body text-brand-offwhite/90">
                Our previous website,{" "}
                <s className="text-brand-gold/60">ebconstructionservices.co.uk</s>,
                has been retired. You are now visiting the official website of{" "}
                <strong className="font-semibold text-brand-gold underline decoration-brand-gold/60 underline-offset-2">
                    EB Construction Ltd
                </strong>
                .
            </span>
        </span>
    );
}

/**
 * A full "iteration" of the track: 3 content units interleaved with gold
 * diamond separators, ending with a trailing diamond so the wrap-around
 * looks natural.
 */
function TrackIteration({ notice }) {
    return (
        <span className="inline-flex items-center gap-6 pr-6">
            {[0, 1, 2].map((i) => (
                <span key={i} className="inline-flex items-center gap-6">
                    <NoticeUnit notice={notice} />
                    <span
                        aria-hidden="true"
                        className="font-body text-[10px] text-brand-gold/60"
                    >
                        ◆
                    </span>
                </span>
            ))}
        </span>
    );
}

export default function SiteNotice() {
    const { pathname } = useLocation();
    const reducedMotion = usePrefersReducedMotion();
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === "undefined") return false;
        return localStorage.getItem(SITE_NOTICE_STORAGE_KEY) === "1";
    });

    const handleDismiss = useCallback(() => {
        try {
            localStorage.setItem(SITE_NOTICE_STORAGE_KEY, "1");
        } catch {
            // Ignore quota / disabled storage — the banner will just come back on next visit.
        }
        setDismissed(true);
    }, []);

    if (!SITE_NOTICES.length) return null;
    if (shouldHide(pathname)) return null;
    if (dismissed) return null;

    const notice = SITE_NOTICES[0];

    return (
        <div
            role="region"
            aria-label="Site announcements"
            className="site-notice-marquee relative w-full overflow-hidden border-t-[3px] border-brand-gold border-b border-brand-line bg-brand-charcoal text-brand-offwhite"
            data-testid="site-notice-slider"
        >
            {/* Subtle gold shimmer sweep — hidden under reduced-motion */}
            {!reducedMotion && (
                <span aria-hidden="true" className="site-notice-shimmer" />
            )}

            {/* Left 24px charcoal fade */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6"
                style={{
                    background:
                        "linear-gradient(to right, rgba(20,24,33,1) 0%, rgba(20,24,33,0) 100%)",
                }}
            />
            {/* Right 24px charcoal fade — nudged in to leave room for the dismiss button */}
            <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-9 z-10 w-6"
                style={{
                    background:
                        "linear-gradient(to left, rgba(20,24,33,1) 0%, rgba(20,24,33,0) 100%)",
                }}
            />

            {/* Live-dot pulse — fixed at the far left, above the fade so it stays visible */}
            <span
                aria-hidden="true"
                data-testid="site-notice-live-dot"
                className="site-notice-livedot pointer-events-none absolute left-3 top-1/2 z-20 inline-block h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand-gold"
            />

            {reducedMotion ? (
                <div className="container-x flex items-center gap-3 py-2.5 pl-8 pr-9">
                    <div className="flex-1 truncate">
                        <NoticeUnit notice={notice} />
                    </div>
                    <DismissButton onClick={handleDismiss} />
                </div>
            ) : (
                <div className="relative flex items-center py-2.5 pl-8 pr-9">
                    <div
                        className="site-notice-track flex min-w-max"
                        data-testid="site-notice-marquee"
                    >
                        <TrackIteration notice={notice} />
                        <TrackIteration notice={notice} aria-hidden="true" />
                    </div>
                    <div className="absolute right-0 z-30 flex h-full items-center pr-3">
                        <DismissButton onClick={handleDismiss} />
                    </div>
                </div>
            )}

            {/* Keyframes + hover-pause + shimmer + pulse — scoped to this component */}
            <style>{`
                @keyframes site-notice-marquee {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .site-notice-marquee .site-notice-track {
                    animation: site-notice-marquee ${MARQUEE_DURATION_S}s linear infinite;
                    animation-play-state: running;
                    will-change: transform;
                }
                .site-notice-marquee:hover .site-notice-track,
                .site-notice-marquee:focus-within .site-notice-track {
                    animation-play-state: paused;
                }

                @keyframes site-notice-pulse {
                    0%, 100% { opacity: 0.4; }
                    50%      { opacity: 1;   }
                }
                .site-notice-marquee .site-notice-livedot {
                    animation: site-notice-pulse 2s ease-in-out infinite;
                }

                @keyframes site-notice-shimmer {
                    from { background-position: -100% 0; }
                    to   { background-position: 100% 0; }
                }
                .site-notice-marquee .site-notice-shimmer {
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                    pointer-events: none;
                    background: linear-gradient(
                        100deg,
                        transparent 0%,
                        transparent 40%,
                        rgba(184, 137, 44, 0.06) 50%,
                        transparent 60%,
                        transparent 100%
                    );
                    background-size: 200% 100%;
                    animation: site-notice-shimmer 12s linear infinite;
                }

                @media (prefers-reduced-motion: reduce) {
                    .site-notice-marquee .site-notice-track {
                        animation: none;
                        transform: none;
                    }
                    .site-notice-marquee .site-notice-livedot {
                        animation: none;
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
}

function DismissButton({ onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label="Dismiss notice"
            className="grid h-7 w-7 shrink-0 place-items-center border border-white/30 bg-brand-charcoal text-brand-offwhite/80 transition-colors hover:border-brand-gold hover:text-brand-gold focus:outline-none focus:border-brand-gold"
            data-testid="site-notice-dismiss"
        >
            <X className="h-3.5 w-3.5" />
        </button>
    );
}
